import React, { useState, useEffect } from 'react'
import { Check, ListFilter, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'

interface FilterDrawerProps {
  isOpen: boolean
  onClose: () => void
  onApplyFilters: (filters: CollegeFilters) => void
  currentFilters: CollegeFilters
}

export interface CollegeFilters {
  status: string[]
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  currentFilters
}) => {
  const [tempFilters, setTempFilters] = useState<CollegeFilters>(currentFilters)

  // Update temp filters when current filters change
  useEffect(() => {
    setTempFilters(currentFilters)
  }, [currentFilters])

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ]

  const handleStatusChange = (statusValue: string, checked: boolean) => {
    setTempFilters(prev => ({
      ...prev,
      status: checked 
        ? [...prev.status, statusValue]
        : prev.status.filter(s => s !== statusValue)
    }))
  }

  const handleApply = () => {
    onApplyFilters(tempFilters)
    onClose()
  }

  const handleClearAll = () => {
    const clearedFilters: CollegeFilters = {
      status: []
    }
    setTempFilters(clearedFilters)
    onApplyFilters(clearedFilters)
    onClose()
  }

  const getActiveFilterCount = () => {
    return tempFilters.status.length
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ListFilter className="h-5 w-5 text-gray-600" />
            Filters
            {getActiveFilterCount() > 0 && (
              <Badge className="h-5 min-w-5 rounded-full px-1 font-mono tabular-nums">
                {getActiveFilterCount()}
              </Badge>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="grid flex-1 auto-rows-min gap-6 px-4">
          {/* Status Filter */}
          <div className="grid gap-3">
            <Label className="text-sm font-medium text-gray-900">Status</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-between font-normal"
                >
                  <span className="truncate">
                    {tempFilters.status.length === 0
                      ? "Select status..."
                      : tempFilters.status.length === 1
                      ? statusOptions.find((option) => option.value === tempFilters.status[0])
                          ?.label
                      : `${tempFilters.status.length} statuses selected`}
                  </span>
                  <div className="flex items-center gap-x-2 text-gray-900">
                    {tempFilters.status.length > 0 && (
                      <Badge className="ml-2 h-5 min-w-5 rounded-full px-1 font-mono tabular-nums">
                        {tempFilters.status.length}
                      </Badge>
                    )}
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  </div>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[22rem] p-3" align="start">
                <div className="space-y-3">
                  <div className="text-xs text-muted-foreground">
                    Select status
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {statusOptions.map((option) => (
                      <div
                        key={option.value}
                        className="flex items-center space-x-3"
                      >
                        <Checkbox
                          id={`status-${option.value}`}
                          checked={tempFilters.status.includes(option.value)}
                          onCheckedChange={(checked) =>
                            handleStatusChange(option.value, !!checked)
                          }
                        />
                        <Label
                          htmlFor={`status-${option.value}`}
                          className="text-sm font-normal cursor-pointer flex-1"
                        >
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <SheetFooter>
          <Button onClick={handleApply} className="w-full">
            <Check className="h-4 w-4" />
            Apply Filters
          </Button>
          <Button
            variant="outline"
            onClick={handleClearAll}
            className="w-full"
            disabled={getActiveFilterCount() === 0}
          >
            Clear All Filters
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default FilterDrawer
